/**
 * Insights Admin — /settings/insights (admin only).
 * Generate drafts via LLM, edit, publish/unpublish, delete.
 */
import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../../lib/api';
import {
  BookOpen, Sparkles, Filter, RefreshCw, Loader2, Eye, EyeOff, Trash2, Edit3,
  ExternalLink, Save, X, ArrowLeft, PlayCircle, Clock, CheckCircle2
} from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from '../../components/ui/alert-dialog';
import { cn } from '../../lib/utils';
import Layout from '../../components/Layout';

const CATEGORY_LABEL = {
  ai_act: 'AI Act',
  nis2: 'NIS2',
  both: 'NIS2 + AI Act',
};

export default function InsightsAdmin() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [generating, setGenerating] = useState(false);
  const [genCategory, setGenCategory] = useState('nis2');
  const [genTopic, setGenTopic] = useState('');
  const [editing, setEditing] = useState(null); // insight being edited
  const [busy, setBusy] = useState({}); // per-row action busy state

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (statusFilter !== 'all') params.status = statusFilter;
      if (categoryFilter !== 'all') params.category = categoryFilter;
      const { data } = await api.get('/insights', { params });
      setItems(data.items);
    } catch (err) {
      toast.error('Falha ao carregar insights.');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post('/insights/generate', {
        category: genCategory,
        topic: genTopic.trim() || undefined,
      });
      toast.success(`Rascunho gerado: "${data.title.slice(0, 60)}..."`);
      setGenTopic('');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha na geração.');
    } finally {
      setGenerating(false);
    }
  };

  const publish = async (it) => {
    setBusy(b => ({ ...b, [it.insight_id]: 'publish' }));
    try {
      await api.post(`/insights/${it.insight_id}/publish`);
      toast.success('Publicado.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao publicar.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[it.insight_id]; return n; });
    }
  };

  const unpublish = async (it) => {
    setBusy(b => ({ ...b, [it.insight_id]: 'unpublish' }));
    try {
      await api.post(`/insights/${it.insight_id}/unpublish`);
      toast.success('Movido para rascunho.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[it.insight_id]; return n; });
    }
  };

  const remove = async (it) => {
    setBusy(b => ({ ...b, [it.insight_id]: 'delete' }));
    try {
      await api.delete(`/insights/${it.insight_id}`);
      toast.success('Removido.');
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha.');
    } finally {
      setBusy(b => { const n = { ...b }; delete n[it.insight_id]; return n; });
    }
  };

  const saveEdit = async () => {
    if (!editing) return;
    try {
      const patch = {
        title: editing.title,
        excerpt: editing.excerpt,
        body_md: editing.body_md,
        tags: (editing.tags_raw || editing.tags?.join(',') || '').split(',').map(s => s.trim()).filter(Boolean),
        hero_emoji: editing.hero_emoji,
        category: editing.category,
      };
      await api.patch(`/insights/${editing.insight_id}`, patch);
      toast.success('Guardado.');
      setEditing(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Falha ao guardar.');
    }
  };

  return (
    <Layout>
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="insights-admin">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <BookOpen className="h-6 w-6 text-indigo-600" /> Insights (blog)
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Gera rascunhos por IA (Claude Sonnet 4.6), edita e publica em{' '}
            <Link to="/insights" className="text-indigo-600 hover:underline inline-flex items-center gap-1">
              /insights <ExternalLink className="h-3 w-3" />
            </Link>
          </p>
        </div>
        <Button variant="ghost" size="sm" onClick={load} data-testid="insights-refresh-btn">
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Generator */}
      <Card className="border-indigo-100 bg-gradient-to-br from-indigo-50/50 to-white">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold text-slate-700 flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-indigo-600" /> Gerar rascunho por IA
          </CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-6 gap-3 items-end">
          <div className="md:col-span-1">
            <Label className="text-xs">Categoria</Label>
            <Select value={genCategory} onValueChange={setGenCategory}>
              <SelectTrigger data-testid="gen-category-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="nis2">NIS2</SelectItem>
                <SelectItem value="ai_act">AI Act</SelectItem>
                <SelectItem value="both">NIS2 + AI Act</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="md:col-span-4">
            <Label className="text-xs">Tema (opcional — deixa vazio para escolher da lista curada)</Label>
            <Input
              value={genTopic}
              onChange={e => setGenTopic(e.target.value)}
              placeholder="Ex: MFA obrigatória sob NIS2 — quando e como implementar"
              data-testid="gen-topic-input"
            />
          </div>
          <div>
            <Button
              onClick={generate}
              disabled={generating}
              className="w-full bg-indigo-600 hover:bg-indigo-700"
              data-testid="gen-btn"
            >
              {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />A gerar…</> : <><PlayCircle className="h-4 w-4 mr-2" />Gerar</>}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-slate-500" />
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-40" data-testid="filter-status"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os estados</SelectItem>
            <SelectItem value="draft">Rascunho</SelectItem>
            <SelectItem value="published">Publicado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={categoryFilter} onValueChange={setCategoryFilter}>
          <SelectTrigger className="w-48" data-testid="filter-category"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas as categorias</SelectItem>
            <SelectItem value="nis2">NIS2</SelectItem>
            <SelectItem value="ai_act">AI Act</SelectItem>
            <SelectItem value="both">NIS2 + AI Act</SelectItem>
          </SelectContent>
        </Select>
        <span className="text-xs text-slate-500 ml-auto">{loading ? '...' : `${items.length} artigos`}</span>
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-sm text-slate-500">
              <Loader2 className="h-6 w-6 animate-spin mx-auto mb-2" />A carregar…
            </div>
          ) : items.length === 0 ? (
            <div className="p-8 text-center text-sm text-slate-500">Sem artigos. Gera o primeiro rascunho acima.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="insights-table">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr className="text-left text-[11px] uppercase tracking-widest text-slate-500">
                    <th className="px-4 py-3 w-8"></th>
                    <th className="px-4 py-3">Título</th>
                    <th className="px-4 py-3">Categoria</th>
                    <th className="px-4 py-3">Estado</th>
                    <th className="px-4 py-3">Views</th>
                    <th className="px-4 py-3">Actualizado</th>
                    <th className="px-4 py-3 text-right">Ações</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {items.map(it => {
                    const isBusy = busy[it.insight_id];
                    const isPublished = it.status === 'published';
                    return (
                      <tr key={it.insight_id} className="hover:bg-slate-50" data-testid={`insight-row-${it.slug}`}>
                        <td className="px-4 py-3 text-2xl align-top">{it.hero_emoji || '📄'}</td>
                        <td className="px-4 py-3">
                          <div className="font-medium text-slate-900 line-clamp-2 max-w-md">{it.title}</div>
                          <div className="text-[11px] text-slate-500 mt-0.5 line-clamp-1 max-w-md">{it.excerpt}</div>
                        </td>
                        <td className="px-4 py-3">
                          <Badge variant="outline" className="text-[10px]">{CATEGORY_LABEL[it.category]}</Badge>
                        </td>
                        <td className="px-4 py-3">
                          {isPublished
                            ? <Badge variant="outline" className="text-[10px] bg-emerald-50 text-emerald-700 border-emerald-200">Publicado</Badge>
                            : <Badge variant="outline" className="text-[10px] bg-slate-50 text-slate-600 border-slate-200">Rascunho</Badge>}
                        </td>
                        <td className="px-4 py-3 text-xs text-slate-600">{it.views_count || 0}</td>
                        <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{(it.updated_at || '').slice(0, 10)}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center justify-end gap-1">
                            {isPublished && (
                              <Button size="sm" variant="ghost" onClick={() => window.open(`/insights/${it.slug}`, '_blank')} title="Ver" data-testid={`insight-view-${it.slug}`}>
                                <ExternalLink className="h-3.5 w-3.5" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => setEditing({ ...it, tags_raw: it.tags?.join(', ') || '' })} title="Editar" data-testid={`insight-edit-${it.slug}`}>
                              <Edit3 className="h-3.5 w-3.5" />
                            </Button>
                            {isPublished ? (
                              <Button size="sm" variant="ghost" onClick={() => unpublish(it)} disabled={!!isBusy} title="Despublicar" data-testid={`insight-unpublish-${it.slug}`}>
                                {isBusy === 'unpublish' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <EyeOff className="h-3.5 w-3.5" />}
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => publish(it)} disabled={!!isBusy} title="Publicar" data-testid={`insight-publish-${it.slug}`}>
                                {isBusy === 'publish' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Eye className="h-3.5 w-3.5 text-emerald-600" />}
                              </Button>
                            )}
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button size="sm" variant="ghost" disabled={!!isBusy} title="Remover" data-testid={`insight-delete-${it.slug}`}>
                                  {isBusy === 'delete' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5 text-red-500" />}
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Remover este insight?</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    &quot;{it.title}&quot; será apagado permanentemente. Contadores de views perdem-se.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancelar</AlertDialogCancel>
                                  <AlertDialogAction onClick={() => remove(it)} className="bg-red-600 hover:bg-red-700">Remover</AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Edit dialog */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="insight-edit-dialog">
          <DialogHeader>
            <DialogTitle>Editar insight</DialogTitle>
            <DialogDescription>Slug: <code className="text-xs">{editing?.slug}</code></DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-6 gap-3">
                <div className="md:col-span-5">
                  <Label className="text-xs">Título</Label>
                  <Input value={editing.title} onChange={e => setEditing({ ...editing, title: e.target.value })} data-testid="edit-title" />
                </div>
                <div>
                  <Label className="text-xs">Emoji</Label>
                  <Input value={editing.hero_emoji || ''} onChange={e => setEditing({ ...editing, hero_emoji: e.target.value })} maxLength={4} data-testid="edit-emoji" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Excerpt</Label>
                <Textarea rows={2} value={editing.excerpt} onChange={e => setEditing({ ...editing, excerpt: e.target.value })} data-testid="edit-excerpt" />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-xs">Categoria</Label>
                  <Select value={editing.category} onValueChange={v => setEditing({ ...editing, category: v })}>
                    <SelectTrigger data-testid="edit-category"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nis2">NIS2</SelectItem>
                      <SelectItem value="ai_act">AI Act</SelectItem>
                      <SelectItem value="both">NIS2 + AI Act</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Tags (vírgula separada)</Label>
                  <Input value={editing.tags_raw} onChange={e => setEditing({ ...editing, tags_raw: e.target.value })} data-testid="edit-tags" />
                </div>
              </div>
              <div>
                <Label className="text-xs">Corpo (Markdown)</Label>
                <Textarea rows={18} value={editing.body_md} onChange={e => setEditing({ ...editing, body_md: e.target.value })} className="font-mono text-xs" data-testid="edit-body" />
                <p className="text-[11px] text-slate-500 mt-1">Suporta Markdown padrão + tabelas, listas, fenced code blocks.</p>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>
              <X className="h-4 w-4 mr-1" /> Cancelar
            </Button>
            <Button onClick={saveEdit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="edit-save-btn">
              <Save className="h-4 w-4 mr-1" /> Guardar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
    </Layout>
  );
}
